// Database utility with typed Firestore collections using existing converters
import { getApps } from "firebase/app";
import { getFirestore, collection, collectionGroup, WithFieldValue, FirestoreDataConverter } from "firebase/firestore";

// Import existing converters
import {
  timesheetConverter,
  clientConverter,
  projectConverter,
  freelancerConverter,
  consultingServicesAgreementConverter,
  freelancerPaymentConverter,
  privateUserConverter,
  invoiceConverter,
  projectAssignmentConverter,
  stakeholderPaymentConverter,
  stakeholderConverter,
} from "../converters";

import {
  boardConverter,
  bugReportConverter,
  featureRequestConverter,
  cardConverter,
  listConverter,
} from "../converters/freelo";

// Import all the model types
import type { IPrivateUserInfo, Freelancer, Stakeholder, Student, PrivateUserInfo } from "../user";
import type {
  Client,
  Project,
  ProjectAssignment,
  ConsultingServicesAgreement,
  ProjectPlan,
  ProjectIdeaData,
  ClientStakeholder,
} from "../projects";
import type { Board, Card, List, CardComment, BoardActivityEvent } from "../freelo";
import type { Timesheet } from "../timesheet";
import type { Payment, FreelancerPayment, StakeholderPayment, ClientInvoiceData } from "../bank";
import type { BlogPost, BlogVersion, Subscriber } from "../blog";
import type { IBugReport, IFeatureRequest } from "../freelo";
import type { INotification } from "../notifications";
import type { Referral } from "../referrals";
import type { SchedulingLink } from "../schedulingLinks";
import type { EmailTemplate, MailDelivery, ContactList } from "../mail";
import { QueryDocumentSnapshot } from "firebase/firestore";
import { dataPointWithConverter, dataPoint, collectionGroupDataPointWithConverter } from "./generics";

const createDb = () => ({
  get blogs() {
    return dataPoint<BlogPost>("blogs");
  },
  get boards() {
    return dataPointWithConverter("boards", boardConverter);
  },
  get bugReports() {
    return dataPointWithConverter("bugReports", bugReportConverter);
  },
  get clients() {
    return dataPointWithConverter("clients", clientConverter);
  },
  get featureRequests() {
    return dataPointWithConverter("featureRequests", featureRequestConverter);
  },
  get notifications() {
    return dataPoint<INotification>("notifications");
  },
  get referrals() {
    return dataPoint<Referral>("referrals");
  },
  get schedulingLinks() {
    return dataPoint<SchedulingLink>("schedulingLinks");
  },
  get subscribers() {
    return dataPoint<Subscriber>("notificationSubscribers");
  },
  get timesheets() {
    return dataPointWithConverter("timesheets", timesheetConverter);
  },
  get users() {
    return dataPointWithConverter("users", privateUserConverter);
  },
  get stakeholders() {
    return dataPointWithConverter("users", stakeholderConverter);
  },
  get students() {
    return dataPoint<Student>("users");
  },
  get freelancers() {
    return dataPointWithConverter("users", freelancerConverter);
  },
  get mailTemplates() {
    return dataPoint<EmailTemplate>("mail_templates");
  },
  get mailDeliveries() {
    return dataPoint<MailDelivery>("mail");
  },
  get contactLists() {
    return dataPoint<ContactList>("contact_lists");
  },

  consultingServicesAgreements: (clientId: string) =>
    dataPointWithConverter(`clients/${clientId}/consultingServicesAgreements`, consultingServicesAgreementConverter),
  invoices: (clientId: string) => dataPointWithConverter(`clients/${clientId}/invoices`, invoiceConverter),
  projectAssignments: (clientId: string) =>
    dataPointWithConverter(`clients/${clientId}/projectAssignments`, projectAssignmentConverter),
  projects: (clientId: string) => dataPointWithConverter(`clients/${clientId}/projects`, projectConverter),
  projectIdeas: (clientId: string) => dataPoint<ProjectIdeaData>(`clients/${clientId}/projectIdeas`),
  projectPlans: (clientId: string) => dataPoint<ProjectPlan>(`clients/${clientId}/projectPlans`),
  stakeholderPayments: (userId: string) =>
    dataPointWithConverter(`users/${userId}/payments`, stakeholderPaymentConverter),
  freelancerPayments: (userId: string) =>
    dataPointWithConverter(`users/${userId}/payments`, freelancerPaymentConverter),
  cards: (boardId: string) => dataPointWithConverter(`boards/${boardId}/cards`, cardConverter),
  deletedCards: (boardId: string) => dataPointWithConverter(`boards/${boardId}/cardTrash`, cardConverter),
  lists: (boardId: string) => dataPointWithConverter(`boards/${boardId}/lists`, listConverter),
  cg: {
    get invoices() {
      return collectionGroupDataPointWithConverter("invoices", invoiceConverter);
    },
    get projectAssignments() {
      return collectionGroupDataPointWithConverter("projectAssignments", projectAssignmentConverter);
    },
    get projects() {
      return collectionGroupDataPointWithConverter("projects", projectConverter);
    },
    get consultingServicesAgreements() {
      return collectionGroupDataPointWithConverter(
        "consultingServicesAgreements",
        consultingServicesAgreementConverter
      );
    },
    get cards() {
      return collectionGroupDataPointWithConverter("cards", cardConverter);
    },
    get deletedCards() {
      return collectionGroupDataPointWithConverter("deletedCards", cardConverter);
    },
    get payments() {
      return collectionGroupDataPointWithConverter("payments", stakeholderPaymentConverter);
    },
  },
});

let _db: ReturnType<typeof createDb> | null = null;

/**
 * Get the database instance (lazy initialization)
 */
const getDb = () => {
  if (!_db) {
    _db = createDb();
  }
  return _db;
};

/**
 * Database with lazy initialization using Proxy - prevents Firebase initialization during import
 */
export const db = new Proxy({} as ReturnType<typeof createDb>, {
  get(target, prop) {
    const database = getDb();
    return database[prop as keyof typeof database];
  },
  has(target, prop) {
    const database = getDb();
    return prop in database;
  },
});

export default db;
